import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createBlock as _createBlock, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  "data-test": "calendar-menu",
  class: "calendar__menu s-p-4 bg-core-neutral-cold-100"
}
const _hoisted_2 = {
  "data-test": "calendar-grid-day",
  class: "calendar__menu__days-container flex flex-col"
}
const _hoisted_3 = {
  "data-test": "calendar-menu-weekdays",
  class: "calendar__menu__weekdays user-select-none"
}
const _hoisted_4 = { class: "calendar__menu__dates user-select-none" }
const _hoisted_5 = ["data-test", "onMouseenter", "onClick"]
const _hoisted_6 = { key: 2 }
const _hoisted_7 = ["data-test", "onMouseenter", "onClick"]
const _hoisted_8 = { key: 1 }
const _hoisted_9 = {
  key: 0,
  class: "calendar__menu__selection-container gap-4"
}

import CalendarDayNumber from 'frontcore/presentation/calendar/views/CalendarDayNumber.vue'
import CalendarHeaderDaySelector from 'frontcore/presentation/calendar/views/CalendarHeaderDaySelector.vue'
import CalendarHeaderMonthSelector from 'frontcore/presentation/calendar/views/CalendarHeaderMonthSelector.vue'
import CalendarHeaderYearSelector from 'frontcore/presentation/calendar/views/CalendarHeaderYearSelector.vue'
import i18n from 'frontcore/i18n'
import RangeSelectorSelectedDate from 'frontcore/presentation/calendar/views/RangeSelectorSelectedDate.vue'
import { CalendarModeOptions, CoreCalendarViewModel, DateRange, DayObject } from 'frontcore/presentation/calendar/domain/types'
import { getEndOfMonth, getEndOfWeek, getEndOfYear, getStartOfWeek, getStartOfYear } from 'frontcore/domain/dates/date-utils'
import { ref }  from 'vue'

interface Props {
  calendarModel: CoreCalendarViewModel
  range?: boolean
  selectionMode?: CalendarModeOptions
  disablePastDays?: boolean
}

export default /*@__PURE__*/_defineComponent({
  __name: 'DropDownCalendar',
  props: {
    calendarModel: {},
    range: { type: Boolean, default: false },
    selectionMode: { default: CalendarModeOptions.SELECT_DAY },
    disablePastDays: { type: Boolean, default: false }
  },
  emits: ["input:value", "selectStartRange", "selectEndRange", "updateMonth", "updateYear", "updateMode"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const hoveredWeekNumber = ref(0)

const emit = __emit

const sortDates = (currentDates: DateRange): DateRange => {
  if (Array.isArray(currentDates) && currentDates.length == 2 && currentDates[0] && currentDates[1]) {
    if (currentDates[1] < currentDates[0]) {
      currentDates[0].setHours(23,59,59,999)
      return [currentDates[1], currentDates[0]]
    }
    currentDates[1].setHours(23,59,59,999)
    return [currentDates[0], currentDates[1]]
  }
  return currentDates
}

const selectWeek = (startDate: Date|null, disabled: boolean) => {
  if (props.selectionMode !== CalendarModeOptions.SELECT_WEEK || startDate === null || disabled) {
    return
  }
  startDate = getStartOfWeek(startDate)
  const endDate = getEndOfWeek(startDate)
  emit('selectEndRange', [startDate, endDate])
}

const selectDate = (date: Date|null, disabled: boolean) => {
  if (date === null || disabled) return
  if (props.selectionMode === CalendarModeOptions.SELECT_WEEK) {
    return selectWeek(date, false)
  }
  if (!props.range) {
    emit('input:value', date)
  }
  else if (!props.calendarModel.startDate
    ||(props.calendarModel.endDate && !isUpdatingEndDate.value)
    || isUpdatingStartDate.value) {
    let selectedRange: DateRange = [date]
    if (props.calendarModel.endDate && isUpdatingStartDate.value) {
      selectedRange = sortDates([date, props.calendarModel.endDate])
    }
    emit('selectStartRange', selectedRange)
    isUpdatingStartDate.value = false
    isUpdatingEndDate.value = true
  } else {
    emit('selectEndRange', sortDates([props.calendarModel.startDate, date]))
    isUpdatingEndDate.value = false
    isUpdatingStartDate.value = true
  }
}

const selectToday = () => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  selectDate(today, props.calendarModel.isTodayDisabled)
}

const isDateDisabled = (calendarDate: DayObject): boolean => {
  if (props.selectionMode === CalendarModeOptions.SELECT_WEEK) {
    return calendarDate.isWeekDisabled
  }
  return calendarDate.isDisabled
}

const selectMonth = () => {
  if (props.selectionMode !== CalendarModeOptions.SELECT_MONTH) {
    return
  }
  const startDate = new Date()
  startDate.setFullYear(props.calendarModel.currentPickerYear, props.calendarModel.currentPickerMonth - 1, 1)
  startDate.setHours(0, 0, 0, 0)
  const endDate = getEndOfMonth(startDate)
  emit('selectEndRange', [startDate, endDate])
}

const { t } = i18n.global

const isUpdatingStartDate = ref(false)
const isUpdatingEndDate = ref(false)

const updateStartDate = () => {
  isUpdatingStartDate.value = true
  isUpdatingEndDate.value = false
}

const updateEndDate = () => {
  if (!props.calendarModel.startDate) return
  isUpdatingEndDate.value = true
  isUpdatingStartDate.value = false
}

const updateMonth = (month: number) => {
  emit('updateMonth', month)
}
const updateMode = (mode: CalendarModeOptions) => {
  emit('updateMode', mode)
}
const updateYear = (year: number) => {
  emit('updateYear', year)
}
const selectYear= (year: number) => {
  if (props.selectionMode !== CalendarModeOptions.SELECT_YEAR) {
    return
  }
  const date = new Date(year, 1, 1)
  const startDate = getStartOfYear(date)
  const endDate = getEndOfYear(date)
  emit('selectEndRange', [startDate, endDate])
}

const handleHover = (weekNumber: number) => {
  if (props.selectionMode !== CalendarModeOptions.SELECT_WEEK) {
    return 0
  }
  hoveredWeekNumber.value = weekNumber
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _withDirectives(_createElementVNode("div", _hoisted_2, [
      _createVNode(CalendarHeaderDaySelector, {
        month: _ctx.calendarModel.currentPickerMonth,
        mode: _ctx.calendarModel.mode,
        selectionMode: _ctx.selectionMode,
        year: _ctx.calendarModel.currentPickerYear,
        "data-test": "calendar-header",
        "onUpdate:mode": updateMode,
        "onUpdate:month": updateMonth,
        "onUpdate:year": updateYear,
        onToday: selectToday
      }, null, 8, ["month", "mode", "selectionMode", "year"]),
      _createElementVNode("ol", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.calendarModel.weekdayNames, (weekday) => {
          return (_openBlock(), _createElementBlock("li", {
            key: weekday,
            class: "calendar__menu__weekday body--auxiliary-01"
          }, _toDisplayString(weekday), 1))
        }), 128))
      ]),
      _createElementVNode("ol", _hoisted_4, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.calendarModel.daysOfLastMonth, (day) => {
          return (_openBlock(), _createElementBlock(_Fragment, null, [
            (day.isFirstDayOfWeek)
              ? (_openBlock(), _createElementBlock("li", {
                  key: 0,
                  "data-test": `calendar-week-number-${day.weekNumber}`,
                  class: _normalizeClass([{
              'calendar__menu__weekday--selected': day.isSelected && _ctx.selectionMode === _unref(CalendarModeOptions).SELECT_WEEK,
              'calendar__menu__weekday--hover': hoveredWeekNumber.value === day.weekNumber,
            }, "calendar__menu__weekday body--auxiliary-01 text-core-neutral-cold-50"]),
                  onMouseenter: ($event: any) => (handleHover(day.weekNumber)),
                  onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (handleHover(0))),
                  onClick: ($event: any) => (selectWeek(day.date, day.isWeekDisabled))
                }, [
                  _createElementVNode("span", null, _toDisplayString(day.weekNumber), 1)
                ], 42, _hoisted_5))
              : _createCommentVNode("", true),
            (day.date)
              ? (_openBlock(), _createBlock(CalendarDayNumber, {
                  key: 1,
                  date: day.date,
                  isCurrentDate: day.isCurrentDate,
                  isDisabled: day.isDisabled,
                  isEndDate: day.isEndDate,
                  isInRange: day.isInRange,
                  isSelected: day.isSelected,
                  isStartDate: day.isStartDate,
                  isLastDayOfWeek: day.isLastDayOfWeek,
                  isHoveredWeek: hoveredWeekNumber.value === day.weekNumber,
                  mode: _ctx.calendarModel.mode,
                  onMouseenter: ($event: any) => (handleHover(day.weekNumber)),
                  onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (handleHover(0))),
                  onClick: _withModifiers(($event: any) => (selectDate(day.date, isDateDisabled(day))), ["stop"])
                }, null, 8, ["date", "isCurrentDate", "isDisabled", "isEndDate", "isInRange", "isSelected", "isStartDate", "isLastDayOfWeek", "isHoveredWeek", "mode", "onMouseenter", "onClick"]))
              : (_openBlock(), _createElementBlock("li", _hoisted_6))
          ], 64))
        }), 256)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.calendarModel.daysInMonth, (day) => {
          return (_openBlock(), _createElementBlock(_Fragment, null, [
            (day.isFirstDayOfWeek)
              ? (_openBlock(), _createElementBlock("li", {
                  key: 0,
                  "data-test": `calendar-week-number-${day.weekNumber}`,
                  class: _normalizeClass([{
              'calendar__menu__weekday--selected': day.isSelected && _ctx.selectionMode === _unref(CalendarModeOptions).SELECT_WEEK,
              'calendar__menu__weekday--hover': hoveredWeekNumber.value === day.weekNumber,
            }, "calendar__menu__weekday body--auxiliary-01 text-core-neutral-cold-50"]),
                  onMouseenter: ($event: any) => (handleHover(day.weekNumber)),
                  onMouseleave: _cache[2] || (_cache[2] = ($event: any) => (handleHover(0))),
                  onClick: ($event: any) => (selectWeek(day.date, day.isWeekDisabled))
                }, [
                  _createElementVNode("span", null, _toDisplayString(day.weekNumber), 1)
                ], 42, _hoisted_7))
              : _createCommentVNode("", true),
            (day.date)
              ? (_openBlock(), _createBlock(CalendarDayNumber, {
                  key: 1,
                  date: day.date,
                  isCurrentDate: day.isCurrentDate,
                  isDisabled: day.isDisabled,
                  isEndDate: day.isEndDate,
                  isInRange: day.isInRange,
                  isSelected: day.isSelected,
                  isStartDate: day.isStartDate,
                  isLastDayOfWeek: day.isLastDayOfWeek,
                  isHoveredWeek: hoveredWeekNumber.value === day.weekNumber,
                  mode: _ctx.calendarModel.mode,
                  onMouseenter: ($event: any) => (handleHover(day.weekNumber)),
                  onMouseleave: _cache[3] || (_cache[3] = ($event: any) => (handleHover(0))),
                  onClick: _withModifiers(($event: any) => (selectDate(day.date, isDateDisabled(day))), ["stop"])
                }, null, 8, ["date", "isCurrentDate", "isDisabled", "isEndDate", "isInRange", "isSelected", "isStartDate", "isLastDayOfWeek", "isHoveredWeek", "mode", "onMouseenter", "onClick"]))
              : _createCommentVNode("", true)
          ], 64))
        }), 256)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.calendarModel.daysOfNextMonth, (day) => {
          return (_openBlock(), _createElementBlock(_Fragment, null, [
            (day.date)
              ? (_openBlock(), _createBlock(CalendarDayNumber, {
                  key: 0,
                  date: day.date,
                  isCurrentDate: day.isCurrentDate,
                  isDisabled: day.isDisabled,
                  isEndDate: day.isEndDate,
                  isInRange: day.isInRange,
                  isSelected: day.isSelected,
                  isStartDate: day.isStartDate,
                  isLastDayOfWeek: day.isLastDayOfWeek,
                  isHoveredWeek: hoveredWeekNumber.value === day.weekNumber,
                  mode: _ctx.calendarModel.mode,
                  onMouseenter: ($event: any) => (handleHover(day.weekNumber)),
                  onMouseleave: _cache[4] || (_cache[4] = ($event: any) => (handleHover(0))),
                  onClick: _withModifiers(($event: any) => (selectDate(day.date, isDateDisabled(day))), ["stop"])
                }, null, 8, ["date", "isCurrentDate", "isDisabled", "isEndDate", "isInRange", "isSelected", "isStartDate", "isLastDayOfWeek", "isHoveredWeek", "mode", "onMouseenter", "onClick"]))
              : (_openBlock(), _createElementBlock("li", _hoisted_8))
          ], 64))
        }), 256))
      ]),
      (_ctx.range)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createVNode(RangeSelectorSelectedDate, {
              class: _normalizeClass({ selected: isUpdatingStartDate.value }),
              value: _ctx.calendarModel.startDate,
              label: _unref(t)('common.messages.from'),
              placeholder: _unref(t)('common.messages.select'),
              dataTest: "selected-start",
              onClick: _withModifiers(updateStartDate, ["stop"])
            }, null, 8, ["class", "value", "label", "placeholder"]),
            _createVNode(RangeSelectorSelectedDate, {
              class: _normalizeClass({ selected: isUpdatingEndDate.value }),
              value: _ctx.calendarModel.endDate,
              label: _unref(t)('common.messages.to'),
              placeholder: _unref(t)('common.messages.select'),
              dataTest: "selected-end",
              onClick: _withModifiers(updateEndDate, ["stop"])
            }, null, 8, ["class", "value", "label", "placeholder"])
          ]))
        : _createCommentVNode("", true)
    ], 512), [
      [_vShow, _ctx.calendarModel.mode === _unref(CalendarModeOptions).SELECT_DAY || _ctx.calendarModel.mode === _unref(CalendarModeOptions).SELECT_WEEK]
    ]),
    _withDirectives(_createVNode(CalendarHeaderMonthSelector, {
      mode: _ctx.calendarModel.mode,
      month: _ctx.calendarModel.currentPickerMonth,
      monthsInYear: _ctx.calendarModel.monthsInYear,
      selectionMode: _ctx.selectionMode,
      year: _ctx.calendarModel.currentPickerYear,
      "data-test": "calendar-grid-month",
      onMonthSelected: selectMonth,
      "onUpdate:mode": updateMode,
      "onUpdate:month": updateMonth,
      "onUpdate:year": updateYear,
      onToday: selectToday
    }, null, 8, ["mode", "month", "monthsInYear", "selectionMode", "year"]), [
      [_vShow, _ctx.calendarModel.mode===_unref(CalendarModeOptions).SELECT_MONTH]
    ]),
    _withDirectives(_createVNode(CalendarHeaderYearSelector, {
      mode: _ctx.calendarModel.mode,
      month: _ctx.calendarModel.currentPickerMonth,
      selectionMode: _ctx.selectionMode,
      year: _ctx.calendarModel.currentPickerYear,
      disablePastDays: _ctx.disablePastDays,
      "data-test": "calendar-grid-year",
      "onUpdate:mode": updateMode,
      "onUpdate:month": updateMonth,
      "onUpdate:year": updateYear,
      "onSelect:year": selectYear,
      onToday: selectToday
    }, null, 8, ["mode", "month", "selectionMode", "year", "disablePastDays"]), [
      [_vShow, _ctx.calendarModel.mode===_unref(CalendarModeOptions).SELECT_YEAR]
    ])
  ]))
}
}

})